/**
 * Mirror of `@arbitrage-core/notifications-core`'s NotificationEvent
 * shape. Kept in sync manually because the frontend doesn't import
 * from the backend workspace; if the backend type evolves, update
 * here too. Common-types is the natural home long-term.
 */

/**
 * The 48 BASE event types. The implicit `entry_` prefix that used to
 * distinguish opener (BUY) from unwinder (SELL) events is GONE — the
 * backend now stamps an explicit `direction: 'entry'|'unwind'|'system'`
 * field on every event and the `type` is the base name only. Read
 * `evt.direction` (not the type prefix) to know which side of the
 * cycle an event is about — see `getNotificationDirection`.
 */
export type NotificationEventType =
  | 'rule_started'
  | 'rule_completed'
  | 'rule_paused'
  | 'rule_cancelled'
  | 'maker_placed'
  | 'maker_adjusted'
  | 'maker_cancelled'
  | 'maker_partial_fill'
  | 'partial_fill_accumulated'
  | 'maker_filled'
  | 'counter_placed'
  | 'counter_filled'
  | 'crossing_executed'
  | 'crossing_partial'
  | 'crossing_accumulated'
  | 'manual_order_placed'
  | 'manual_order_executed'
  | 'manual_order_partial_fill'
  | 'execution_complete'
  | 'error'
  | 'counter_failed'
  | 'out_of_balance'
  | 'rule_below_minimum'
  | 'dust_remaining'
  | 'market_takeover'
  | 'tna_negative'
  | 'net_contracts_digest'
  | 'emergency_stop'
  | 'emergency_resume'
  | 'recovery_filled'
  | 'recovery_restored'
  | 'counter_skipped'
  | 'max_volume_reached'
  | 'maker_yielded_to_counter'
  | 'platform_disconnect'
  | 'platform_reconnect'
  | 'book_stale'
  | 'book_fresh'
  | 'maker_quiet_cancel'
  | 'constraint_violated'
  | 'over_buy'
  | 'over_sell'
  | 'leg_imbalance'
  | 'engine_live_task_panic'
  | 'order_rejected_rate_limited'
  | 'order_rate_limit_waiting'
  | 'market_closed'
  | 'maker_cross_refused'
  | 'counter_stuck'
  | 'counter_market_hedge'
  | 'counter_maker_walk_step'
  | 'entry_counter_maker_walk_step'
  | 'hedge_deferred'
  | 'mode_change'
  | 'hedge_unhedged_panic'
  | 'belief_position_drift'
  | 'pair_missing_token_ids'
  | 'unresolved_fill_naked_leg'
  // 2026-07-31 — a trade the engine BOOKED (and hedged) later DIED at the
  // venue: the booked leg is gone while the irreversible hedge remains.
  // Same CRITICAL naked-leg class as `unresolved_fill_naked_leg`.
  | 'dead_trade_naked_leg'
  | 'orphaned_automation_fill'
  | 'rule_state_stale'
  | 'rule_state_recovered'
  | 'book_cross_saved'
  // All-clear for the boot-time system-config fetch: a pod that was reported as
  // running on guessed hedge config has read the real singleton.
  | 'system_config_recovered'
  // 2026-08-24 — Slack DELIVERY health. Slack answered every webhook POST with
  // `429 message_limit_exceeded` for ~8h and ~1.000 cards were silently lost
  // (a failed POST is discarded — no retry, no queue). These arrive over the
  // in-app feed precisely BECAUSE Slack is the broken transport.
  | 'slack_budget_warning'
  | 'slack_delivery_failing'
  | 'slack_delivery_recovered'
  // 2026-06-04 — terminal teardown events from the Rust engine (were being
  // dropped by the dispatcher until routing was added).
  | 'session_summary'
  | 'naked_remainder'
  | 'grace_hedge'
  | 'starting_position_imbalance'
  // 2026-07-03 — hedge-accounting events from the Rust engine.
  | 'over_hedged'
  | 'counter_credit_applied'
  // 2026-07-10 — counter-order lost-ACK reconciliation events.
  | 'counter_recovered'
  | 'counter_rearmed'
  | 'counter_unconfirmed'
  // 2026-07-22 — a stuck counter-hedge escalation declined to cross the book
  // above the rule's threshold / max-sum cap and left the remainder resting
  // as a GTC maker instead of taking.
  | 'counter_capped_resting'
  // 2026-07-20 — Sniper (live-event directional sweep) engine events. Surface
  // in the existing prediction-notifications feed (see `sniper-contract.md`).
  | 'sniper_fired'
  | 'sniper_auto_no_fired'
  | 'sniper_var_reversal'
  | 'sniper_var_rebuy'
  // 2026-08-02 — LSports RETRACTED a goal we hold a position from: the result is in
  // doubt and an exit is running. Carries the retraction's own `confidence`.
  | 'sniper_goal_cancelled'
  | 'sniper_fire_blocked'
  // 2026-07-29 — the Kalshi confidence ladder's resting exit order.
  | 'sniper_ladder_sell_placed'
  | 'sniper_ladder_sell_amended'
  | 'sniper_ladder_sell_filled'
  // 2026-08-01 — every booked BUY fill delta on a sniper order. Carries the
  // cumulative `filled` + `sizeRequested`, so partial-vs-complete is DERIVED
  // (`filled < sizeRequested`) rather than being a second event type.
  | 'sniper_buy_filled'
  // 2026-08-06 — Polymarket cancel-path events from the pending-cancel reaper.
  // The reaper re-issues a cancel for every fire-and-forget Poly cancel the
  // venue still shows resting; these are its escalation ladder.
  | 'poly_cancel_unconfirmed'
  | 'poly_cancel_abandoned'
  | 'poly_pending_cancel_registry_overflow'
  // 2026-08-06 — a Polymarket cancel+replace whose cancel could NOT be
  // confirmed off-book: the replacement is deliberately withheld so two live
  // makers can never rest on the same leg. The leg is left PARKED (inert), so
  // this is operator-visible — but it is a defensive decision working as
  // designed, not a fault, hence `warning` rather than `error`.
  | 'poly_replace_withheld'
  // 2026-08-13 — the opener's forfait protection armed: a pair collapsed to a
  // 50-50 resolution regime and the engine suspended the paths whose
  // Polymarket leg would enter above the cap. The existing position is NOT
  // unwound.
  | 'forfait_protection_triggered'
  // 2026-08-18 — the PROVISIONAL half of that brake. The cycle detector tripped
  // and entry is withheld, but nothing was written and the rule was not paused:
  // one hold window later the engine re-reads the book and either arms for real
  // (`forfait_protection_triggered`) or releases and resumes
  // (`forfait_hold_released`). `forfait_hold_started` repeats ONCE, with
  // `heldMs` set, when the hold has gone its ceiling without reaching a verdict
  // — whether or not the book reads, which `bookReadable` says; the brake stays
  // on either way, and that card is what keeps it from being invisible.
  //
  // `forfait_hold_dropped` is the third outcome and NOT a shape of the released
  // one: the hold was let go because the rule stopped holding, so no verdict was
  // taken. The release asserts a finding ("no longer closing on $0.50"); this
  // asserts nothing, and a separate type keeps it off the all-clear's green.
  | 'forfait_hold_started'
  | 'forfait_hold_released'
  | 'forfait_hold_dropped'
  // 2026-08-13 — the venue-maintenance lifecycle. A scheduled Kalshi/Polymarket
  // maintenance window used to reach the operator only as the SYMPTOM: dozens
  // of `poly_cancel_unconfirmed` cards, one per resting order, every two
  // minutes, with no closing event. These three replace it with a halt, a
  // resume, and a "the status feed is unreadable so the protection is blind".
  | 'venue_maintenance_halt'
  | 'venue_maintenance_panic'
  | 'venue_maintenance_window_over'
  | 'venue_maintenance_resumed'
  | 'venue_maintenance_probe_blind';

/**
 * Curated allowlist of "social-style" notification types.
 *
 * These are NOT per-operation trading-lifecycle events (fills, orders,
 * amends, rule bookends) — they are account / portfolio-level alerts the
 * operator wants surfaced like a social-network notification: a sidebar
 * bell with an unread badge + dropdown, NOT a row in the dense operations
 * feed at `/predictions/notifications`.
 *
 * Membership is the single source of truth for the split:
 *   - The operations feed EXCLUDES every type in this set (REST query +
 *     realtime prepend + type-filter dropdown) so they "move out entirely".
 *   - The sidebar bell (`useSocialNotifications` / `NotificationBell`)
 *     shows ONLY the types in this set.
 *
 * To promote a future event type into the bell, add it here — both
 * surfaces react automatically. Starts with `tna_negative`.
 */
export const SOCIAL_NOTIFICATION_TYPES: readonly NotificationEventType[] = [
  'tna_negative',
];

const SOCIAL_NOTIFICATION_TYPE_SET: ReadonlySet<string> = new Set(
  SOCIAL_NOTIFICATION_TYPES,
);

/** True when an event type belongs on the sidebar bell, not the ops feed. */
export function isSocialNotification(
  type: NotificationEventType | string,
): boolean {
  return SOCIAL_NOTIFICATION_TYPE_SET.has(type);
}

/**
 * Three-valued ON PURPOSE, and NOT a mirror of the Rust wire enum.
 *
 * The engine emits a fourth value, `critical`, but both engine-boundary
 * consumers collapse it to `error` before anything is published
 * (`notification-stream.consumer.ts` in position-opener / position-unwinder,
 * pinned by their specs), `notifications-core`'s `Severity` is 3-valued, and
 * all eight Mongo schemas enum-forbid `critical`. So it can never reach this
 * app. Adding it here would produce a Severity filter option that silently
 * matches zero rows — a false negative in an incident-triage UI.
 */
export type NotificationSeverity = 'info' | 'warning' | 'error';
export type NotificationSource = 'automation' | 'manual' | 'system';
export type NotificationPlatform = 'kalshi' | 'polymarket';

export interface NotificationEvent<TPayload = Record<string, unknown>> {
  id: string;
  type: NotificationEventType;
  /**
   * Which side of the arbitrage cycle this event belongs to. Stamped
   * explicitly by the backend dispatcher (replaced the implicit
   * `entry_` type-name prefix). Optional only so a not-yet-migrated
   * in-flight doc still type-checks; `getNotificationDirection` falls
   * back to a system/unwind default when it's absent.
   */
  direction?: NotificationDirection;
  severity: NotificationSeverity;
  source: NotificationSource;
  occurredAt: string;
  /**
   * Per-rule monotonic sequence stamped by the backend dispatcher.
   * Present iff the event has a `ruleId`. Used as the secondary
   * sort key behind `occurredAt` so two events for the same rule
   * fired in the same JS tick on the producer (canonical case:
   * `counter_filled` → `execution_complete`) render in source order
   * regardless of WS arrival order. System / manual events have no
   * `seq` because they have no `ruleId` and cross-rule ordering is
   * not a logical invariant.
   */
  seq?: number | null;
  /**
   * Global Redis-Stream cursor — the `notifications:stream` entry id
   * (`<ms>-<seq>`). Assigned by the realtime relay when it reads the
   * event from the stream; NOT set by producers, the dispatcher, or
   * the Mongo/REST history. Present only on the live socket frame +
   * replay. The FE stores it as its replay cursor and prefers it as
   * the global total-order sort key for live rows.
   */
  streamId?: string | null;
  /**
   * Per-(rule, engine eval pass) grouping id stamped by the Rust engine —
   * which eval pass produced this burst of events for the rule. Tracing /
   * grouping metadata ONLY, NOT a delivery or ordering primitive (ordering is
   * `occurredAt`/`seq`). Absent for system/health events and non-engine
   * producers.
   */
  tickId?: string | null;
  /**
   * Order of this event WITHIN its `tickId` — strictly increasing within a
   * pass, re-based each pass. Present iff `tickId` is.
   */
  intraTickSeq?: number | null;
  /**
   * Optional trace link to the specific order this event concerns. Most
   * engine payloads already carry `payload.orderId`; this hoists it for
   * tracing. NOT a delivery or ordering primitive.
   */
  orderId?: string | null;
  ruleId?: string | null;
  pairId?: string | null;
  pairName?: string | null;
  platform?: NotificationPlatform | string | null;
  kalshiTicker?: string | null;
  polyConditionId?: string | null;
  payload: TPayload;
}

/**
 * Direction axis: which side of the arbitrage cycle a notification is
 * about. Mirrors the same enum in `notification-display.ts`. Kept in
 * the type module so filter state is fully typed without importing
 * from the components folder.
 */
export type NotificationDirection = 'entry' | 'unwind' | 'system';

/** Filter state held in URL search params + the filters bar. */
export interface NotificationsFilter {
  from?: string;
  to?: string;
  ruleId?: string;
  pairId?: string;
  types?: NotificationEventType[];
  /**
   * Types to hide from the feed (Mongo `$nin`). Used to hide high-volume /
   * low-value events (maker amends) by default without enumerating every
   * other type. ANDed with `types` on the backend.
   */
  excludeTypes?: NotificationEventType[];
  direction?: NotificationDirection | 'all';
  platform?: NotificationPlatform | 'all';
  source?: NotificationSource | 'all';
  severity?: NotificationSeverity | 'all';
}

export interface NotificationsListResponse {
  items: NotificationEvent[];
  nextCursor: string | null;
  count: number;
}

/**
 * The closed vocabulary of sweep-mode operating modes carried by a
 * `mode_change` notification's `toMode` / `fromMode`. Mirrors the Rust
 * engine's mode strings. `sweep_dual_post` is the legacy (deleted 2026-07)
 * overlay mode, kept so historical cards still render.
 */
export type SweepMode =
  | 'maker_first'
  | 'sweep_rising_leg_first'
  | 'paused'
  | 'sweep_dual_post';

/**
 * The ONLY valid values of a `mode_change` EXIT card's `reason` — the
 * Rust `SweepStopReason` closed vocabulary. Shared with the engine (the
 * engine has a compile-time test asserting the two lists agree). Anything
 * else on the wire is treated as a legacy/free-form reason by the FE.
 */
export type SweepStopReason =
  | 'sweep_stopped'
  | 'edge_gone'
  | 'budget_exhausted'
  | 'naked_remainder'
  | 'hedge_failure'
  | 'maxv_reached';

/**
 * Structured payload of a `mode_change` notification, as emitted by the
 * rising-leg-first sweep engine (Unit P2E). All keys are optional so a
 * legacy card (which carried none of them) still type-checks and the FE
 * falls back to `title`/`message`.
 *
 * Three shapes share this type, disambiguated by `toMode` / `reason`:
 *   - ENTRY   — `toMode: 'sweep_rising_leg_first'`, no stop `reason`.
 *   - REVERSAL — `reason: 'reversed'` (the rising leg changed under us).
 *   - EXIT    — `toMode: 'maker_first' | 'paused'`, `reason` a SweepStopReason.
 */
export interface ModeChangeNotificationPayload {
  // Common
  fromMode?: SweepMode | string;
  toMode?: SweepMode | string;
  reason?: SweepStopReason | 'reversed' | string;
  path?: string;
  severity?: NotificationSeverity;
  /** Backend dedup backstop: how many identical cards this one folds in. */
  suppressedCount?: number;

  // ENTRY
  risingLeg?: string;
  fallingLeg?: string;
  risingSide?: string;
  sweepDirection?: string;
  velocityTicksPer100ms?: number;
  inferred?: boolean;
  contractsSwept?: number;
  frontPrice?: number;

  // REVERSAL
  oldRisingLeg?: string;
  newRisingLeg?: string;
  ownedOldRisingVwap?: number;
  reTargetCeiling?: number;

  // EXIT summary
  durationMs?: number;
  reversals?: number;
  contractsTaken?: number;
  contractsHedged?: number;
  unhedgedRemainder?: number;
  takes?: number;
  refiresSuppressed?: number;

  // Legacy fallback text (historically formatted into title/message).
  title?: string;
  message?: string;
  detail?: string;
}
